/**
 * S10 — LA FAMIGLIA (the real crew)
 *
 * Real team lineup: Giancarlo Loffredo, Nelo G, JN Silva, Tomall.
 * Serif name + mono role, no photos yet (placeholder card frames).
 * Signature flame-orange knife-cut divider on section entry.
 */

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy, crew } from "@/lib/voice";
import giancarloSrc from "@/assets/teamportraits/giancarlo.webp";
import neloSrc from "@/assets/teamportraits/nelo.webp";
import jnsilvaSrc from "@/assets/teamportraits/jnsilva.webp";
import tomallSrc from "@/assets/teamportraits/tomall.webp";

const portraitMap: Record<string, string> = {
  giancarlo: giancarloSrc,
  nelo: neloSrc,
  jnsilva: jnsilvaSrc,
  tomall: tomallSrc,
};

gsap.registerPlugin(ScrollTrigger);

export function S10_TheCanon() {
  const copy = sectionCopy.s10;
  const sectionRef = useRef<HTMLElement | null>(null);
  const cutMaskRef = useRef<HTMLDivElement | null>(null);
  const cutLineRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          end: "top 20%",
          scrub: 1,
        },
      });
      tl.fromTo(
        cutLineRef.current,
        { scaleX: 0, transformOrigin: "left center" },
        { scaleX: 1, duration: 0.5, ease: "power2.in" },
      ).fromTo(
        cutMaskRef.current,
        { scaleX: 1, transformOrigin: "right center" },
        { scaleX: 0, duration: 1, ease: "power3.inOut" },
        "-=0.1",
      );

      cardRefs.current.forEach((card) => {
        if (!card) return;
        gsap.fromTo(
          card,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              end: "top 55%",
              scrub: 1,
            },
          },
        );
      });
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="s10"
      className="section"
      style={{
        backgroundColor: "var(--ink)",
        color: "var(--bone)",
        flexDirection: "column",
        padding: "14vh var(--space-lg) 12vh",
        minHeight: "auto",
      }}
    >
      {/* Knife-cut wipe */}
      <div
        ref={cutMaskRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "var(--ink)",
          zIndex: 5,
        }}
      />
      <div
        ref={cutLineRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          left: 0,
          width: "100%",
          height: "4px",
          background: "var(--flame)",
          boxShadow: "0 0 40px var(--flame)",
          zIndex: 6,
          transform: "scaleX(0)",
        }}
      />

      <MonumentType word={copy.monument} theme="ink" />

      {/* Header */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          marginBottom: "var(--space-2xl)",
        }}
      >
        <div
          className="type-meta-sm"
          style={{
            color: "var(--flame)",
            marginBottom: "var(--space-md)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {copy.meta}
        </div>
        <h2
          className="type-hero-lg"
          style={{ color: "var(--bone)", margin: 0 }}
        >
          {copy.hero}
        </h2>
        {copy.subhero && (
          <p
            className="type-voice"
            style={{
              color: "var(--fog)",
              marginTop: "var(--space-md)",
              fontSize: "clamp(1.125rem, 1.4vw, 1.5rem)",
              fontStyle: "italic",
            }}
          >
            {copy.subhero}
          </p>
        )}
      </div>

      {/* Crew grid */}
      <div
        className="s10-crew"
        style={{
          position: "relative",
          zIndex: 3,
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "var(--space-lg)",
          width: "min(1300px, 92vw)",
          margin: "0 auto",
        }}
      >
        {crew.map((member, i) => (
          <div
            key={member.name}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            className="s10-crew-card"
            style={{
              position: "relative",
            }}
          >
            {/* Number */}
            <div
              className="type-meta-sm"
              style={{
                color: "var(--flame)",
                letterSpacing: "var(--ls-meta-wide)",
                marginBottom: "var(--space-sm)",
              }}
            >
              / 0{i + 1}
            </div>

            {/* Portrait — square, grayscale, subtle contrast on hover */}
            <div
              className="s10-portrait-wrap"
              style={{
                position: "relative",
                aspectRatio: "1 / 1",
                overflow: "hidden",
                marginBottom: "var(--space-md)",
                border: "1px solid rgba(232, 106, 29, 0.3)",
                background: "var(--oil)",
              }}
            >
              <img
                src={portraitMap[member.portrait]}
                alt={member.name}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: "grayscale(100%) contrast(1.1) brightness(0.88)",
                  transition:
                    "filter 0.8s var(--ease-cinematic), transform 1.2s var(--ease-cinematic)",
                }}
              />
              {/* Subtle flame-orange tint overlay (very low opacity) */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(5,5,5,0) 55%, rgba(5,5,5,0.75) 100%)",
                  pointerEvents: "none",
                }}
              />
            </div>

            {/* Name */}
            <div
              style={{
                fontFamily: "var(--f-serif-display)",
                fontWeight: 900,
                fontStretch: "75%",
                fontSize: "clamp(1.25rem, 1.9vw, 1.875rem)",
                lineHeight: 0.95,
                letterSpacing: "-0.01em",
                color: "var(--bone)",
                textTransform: "uppercase",
                marginBottom: "var(--space-sm)",
                minHeight: "2.4em",
              }}
            >
              {member.name}
            </div>

            {/* Role */}
            <div
              className="type-meta-sm"
              style={{
                color: "var(--fog)",
                letterSpacing: "var(--ls-meta-wide)",
                paddingTop: "var(--space-sm)",
                borderTop: "1px solid rgba(232, 106, 29, 0.25)",
              }}
            >
              {member.role}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
